/**
 * What a thing is for, and where another one comes from.
 *
 * The skill guide answers "what does this skill open", which is what somebody
 * asks while choosing what to train. This answers what somebody asks while
 * holding an unfamiliar item, and it is the same question in both directions:
 * what makes this, and what does this make.
 *
 * Opened from an item's own menu, and from a search field for anything not in
 * the bag. Everything in it is read off the definitions the client already
 * holds, so a recipe written in the tools turns up the moment it is saved.
 */

import { useState } from 'react';
import type { ItemDefinitionWire } from '@inverse/protocol';
import { CloseButton } from './CloseButton';
import { useDraggableWindow } from './draggable';
import { useAltHeld } from './alt-mode';
import { useWindowGrips } from './grips';
import { itemsLike, lookUp, type Making, type Shelf } from './lookup';

function Ways({ title, ways }: { title: string; ways: readonly Making[] }): JSX.Element | null {
  if (ways.length === 0) return null;

  return (
    <>
      <h3 className="lookup-head">{title}</h3>
      {ways.map((way, at) => (
        <div className="guide-row" key={`${way.gives}-${way.takes}-${at}`}>
          <span className="guide-level">{way.level}</span>

          <span className="guide-what">
            <b>
              {way.gives ? `${way.takes} \u2192 ${way.gives}` : `${way.action} ${way.takes}`}
            </b>
            <small>
              {way.skill}
              {way.where ? ` at the ${way.where}` : ', anywhere'}
            </small>
            {way.alsoNeeds.length > 0 ? <small>{way.alsoNeeds.join(', ')}</small> : null}
          </span>

          <span className="guide-xp">{Math.round(way.experience)} xp</span>
        </div>
      ))}
    </>
  );
}

export function Lookup({
  itemId,
  shelf,
  onLookUp,
  onClose,
}: {
  itemId: number;
  shelf: Shelf;
  /** Following a name in the book to whatever it names. */
  onLookUp: (itemId: number) => void;
  onClose: () => void;
}): JSX.Element {
  const { attach, style, onPointerDown, nudge } = useDraggableWindow('lookup');
  const altHeld = useAltHeld();
  const { size: windowSize, grips } = useWindowGrips('lookup', nudge, altHeld, {
    width: 320,
    height: 260,
  });

  const [typed, setTyped] = useState('');
  const found: ItemDefinitionWire[] = itemsLike(typed, shelf.items);
  const held = lookUp(itemId, shelf);

  return (
    <div
      className={`trade skill-guide${altHeld ? ' ui-mode' : ''}${windowSize ? ' sized' : ''}`}
      ref={attach}
      style={{ ...style, ...(windowSize ?? {}) }}
    >
      <div className="panel-head drag-handle" onPointerDown={onPointerDown}>
        <span className="guide-title">{held ? held.name : 'What is this for?'}</span>
        <CloseButton onClose={onClose} />
      </div>

      <div className="panel-body">
        <input
          id="lookup-search"
          className="lookup-search"
          type="search"
          value={typed}
          placeholder="Look something up"
          onChange={(event) => setTyped(event.target.value)}
        />

        {/* What was typed, offered as a list. Choosing one looks it up and
            clears the field, so the book shows the thing rather than the
            search that found it. */}
        {found.length > 0 ? (
          <div className="lookup-found">
            {found.map((one) => (
              <button
                type="button"
                key={one.id}
                className="lookup-hit"
                onClick={() => {
                  onLookUp(one.id);
                  setTyped('');
                }}
              >
                {one.name}
              </button>
            ))}
          </div>
        ) : null}

        {held === null ? (
          <p className="hint">Nothing is written about that.</p>
        ) : (
          <>
            {held.examine ? <p className="hint">{held.examine}</p> : null}

            <Ways title="Made by" ways={held.madeBy} />
            <Ways title="Used in" ways={held.usedIn} />

            {held.gatheredFrom.length > 0 ? (
              <>
                <h3 className="lookup-head">Gathered from</h3>
                <div className="guide-row">
                  <span className="guide-level">{held.gatherLevel > 0 ? held.gatherLevel : ''}</span>
                  <span className="guide-what">
                    <b>{held.gatheredFrom.join(', ')}</b>
                  </span>
                </div>
              </>
            ) : null}

            {held.madeBy.length === 0 &&
            held.usedIn.length === 0 &&
            held.gatheredFrom.length === 0 ? (
              <p className="hint">
                Nothing makes it, nothing is made from it, and it is not gathered anywhere.
              </p>
            ) : null}

            <p className="hint lookup-worth">
              {held.kind}
              {held.value > 0 ? ` · worth ${held.value.toLocaleString()} coins` : ''}
            </p>
          </>
        )}
      </div>
      {grips}
    </div>
  );
}
